import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Box from '@mui/material/Box';
import Collapse from '@mui/material/Collapse';
import Typography from '@mui/material/Typography';
import React, { useId, useState } from 'react';

import { PanelProps } from './Panel.props';

export const Panel: React.FC<PanelProps> = ({
  title,
  actions,
  children,
  sx,
  bodyPadding = '12px 14px',
  overflow = 'hidden',
  collapsible = false,
  defaultExpanded = false,
  titleAdornment,
}) => {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const bodyId = useId();
  const isOpen = !collapsible || expanded;

  const heading = (
    <>
      <Typography sx={{ fontSize: '14px', fontWeight: 600 }}>{title}</Typography>
      {titleAdornment}
    </>
  );

  return (
    <Box
      sx={{
        bgcolor: 'background.paper',
        border: 1,
        borderColor: 'v4.border',
        borderRadius: 2,
        overflow,
        ...sx,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
          px: '14px',
          py: '10px',
          borderBottom: 1,
          // A shut panel is a single bar, so the rule under the header would
          // read as a stray line rather than a divider.
          borderColor: isOpen ? 'v4.border' : 'transparent',
        }}
      >
        {collapsible ? (
          <Box
            component="button"
            type="button"
            aria-expanded={expanded}
            aria-controls={bodyId}
            onClick={() => setExpanded((prev) => !prev)}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              flex: 1,
              minWidth: 0,
              p: 0,
              border: 'none',
              background: 'none',
              font: 'inherit',
              color: 'inherit',
              textAlign: 'left',
              cursor: 'pointer',
              '&:focus-visible': {
                outline: '2px solid',
                outlineColor: 'v4.accent',
                outlineOffset: 2,
                borderRadius: '4px',
              },
            }}
          >
            <ExpandMoreIcon
              sx={{
                fontSize: 18,
                color: 'text.secondary',
                transform: expanded ? 'rotate(0deg)' : 'rotate(-90deg)',
                transition: 'transform 150ms',
                '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
              }}
            />
            {heading}
          </Box>
        ) : (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>{heading}</Box>
        )}
        {actions && <Box>{actions}</Box>}
      </Box>
      {collapsible ? (
        <Collapse in={expanded} unmountOnExit>
          <Box id={bodyId} sx={{ p: bodyPadding }}>
            {children}
          </Box>
        </Collapse>
      ) : (
        <Box sx={{ p: bodyPadding }}>{children}</Box>
      )}
    </Box>
  );
};
